import React, { useState, useEffect } from 'react';
import { Sidebar } from './Dashboard';
import BenchmarkTabs from './BenchmarkTabs';
import BenchmarkPicker from '../BenchmarkPicker';
import { API } from '../App';

const CODE_DESCRIPTIONS = {
  TCO: 'Time Cards Out',
  TTD: 'Time to Draw',
  TPU: 'Time Pick Up',
  // Roulette breakdown codes
  SPT: 'Spin Time',
  SWT: 'Sweep Time',
  OPT: 'Outside Pay Time',
  IPT: 'Inside Pay Time',
  PTT: 'Pay/Take Time',
  // Baccarat breakdown codes
  DDT: 'Dealer Draw Time',
  SBP: 'Side Bet Pay Time',
  MBP: 'Main Bet Pay Time',
  CUT: 'Cards Up Time',
  ABP: 'All Bet Pay Time',
  // Carnival breakdown codes
  PAT: 'Player Action Time',
  DAT: 'Dealer Action Time',
  PYT: 'Payout Time',
  TBR: 'Time Between Rounds',
  DPT: 'Deal & Pay Time',
  TRT: 'Total Round Time',
  TST: 'Total Shuffle Time',
  AST: 'After Shuffle Time',
  TCT: 'Total Change Time',
  ACT: 'After Change Time',
  OTT: 'Other Pause Time',
  AOT: 'After Other Time',
};

const PREFERRED_CODE_ORDER = ['TCO', 'TTD', 'TPU', 'SPT', 'SWT', 'OPT', 'IPT', 'PTT', 'DDT', 'SBP', 'MBP', 'CUT', 'ABP', 'PAT', 'DAT', 'PYT', 'DPT', 'TBR', 'TRT', 'TST', 'AST', 'TCT', 'ACT', 'OTT', 'AOT'];

// Legacy fallback when a game has no benchmark_dimensions in form_config
const FALLBACK_DIMENSIONS = {
  TCO: ['v1'], TTD: ['v1'], TBR: ['v1'],
  TPU: ['v1', 'v2', 'v4'], DPT: ['v1', 'v2', 'v4'], TRT: ['v1', 'v2', 'v4'],
  TST: [], AST: [], TCT: [], ACT: [],
};

// A combo key is the cell's values joined, in the order of the code's dimensions:
// "5", "5|Complex", "5|Mid|Complex". They are stored in the three value columns in
// that same order, which is how the benchmark engine reads them back.
function comboParts(varKey) {
  const parts = varKey.split('|');
  return { v1: parts[0] || null, v2: parts[1] || null, v4: parts[2] || null };
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#0f0f1a',
    display: 'flex',
  },
  main: {
    flex: 1,
    padding: '40px',
    overflowY: 'auto',
  },
  pageTitle: {
    color: '#ffffff',
    fontSize: '28px',
    fontWeight: 'bold',
    marginBottom: '8px',
  },
  pageSubtitle: {
    color: '#a0a0b0',
    fontSize: '14px',
    marginBottom: '40px',
  },
  card: {
    backgroundColor: '#16213e',
    borderRadius: '16px',
    padding: '24px',
    marginBottom: '24px',
  },
  cardTitle: {
    color: '#ffffff',
    fontSize: '18px',
    fontWeight: 'bold',
    marginBottom: '4px',
  },
  cardSubtitle: {
    color: '#a0a0b0',
    fontSize: '13px',
    marginBottom: '24px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    color: '#a0a0b0',
    fontSize: '12px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '12px 16px',
    textAlign: 'left',
    borderBottom: '1px solid #0f3460',
  },
  td: {
    color: '#ffffff',
    fontSize: '14px',
    padding: '14px 16px',
    borderBottom: '1px solid #0f3460',
    verticalAlign: 'middle',
  },
  tdVar: {
    color: '#a0a0b0',
    fontSize: '13px',
    padding: '8px 16px 8px 32px',
    borderBottom: '1px solid #0a1a30',
    verticalAlign: 'middle',
    backgroundColor: '#0d1a2e',
  },
  codeLabel: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  codeDesc: {
    color: '#a0a0b0',
    fontSize: '12px',
    marginTop: '2px',
  },
  input: {
    width: '100px',
    padding: '8px 12px',
    backgroundColor: '#0f3460',
    border: '1px solid #0f3460',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    boxSizing: 'border-box',
    outline: 'none',
  },
  toggleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  toggleLabel: {
    color: '#a0a0b0',
    fontSize: '12px',
  },
  toggleOff: {
    width: '36px',
    height: '20px',
    backgroundColor: '#0f3460',
    borderRadius: '10px',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    position: 'relative',
  },
  toggleOn: {
    width: '36px',
    height: '20px',
    backgroundColor: '#e94560',
    borderRadius: '10px',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    position: 'relative',
  },
  saveButton: {
    padding: '12px 32px',
    backgroundColor: '#e94560',
    border: 'none',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginTop: '16px',
  },
  success: {
    color: '#00cc00',
    fontSize: '14px',
    marginBottom: '16px',
  },
  error: {
    color: '#e94560',
    fontSize: '14px',
    marginBottom: '16px',
  },
  gameTab: {
    padding: '10px 20px',
    backgroundColor: '#0f3460',
    border: '2px solid #0f3460',
    borderRadius: '8px',
    color: '#a0a0b0',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginRight: '8px',
    marginBottom: '24px',
  },
  gameTabActive: {
    padding: '10px 20px',
    backgroundColor: '#0f3460',
    border: '2px solid #e94560',
    borderRadius: '8px',
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    marginRight: '8px',
    marginBottom: '24px',
  },
  expandButton: {
    padding: '4px 10px',
    backgroundColor: 'transparent',
    border: '1px solid #0f3460',
    borderRadius: '6px',
    color: '#a0a0b0',
    fontSize: '11px',
    cursor: 'pointer',
    marginLeft: '8px',
  },
  autoValue: {
    color: '#a0a0b0',
    fontSize: '13px',
  },
  recordCount: {
    color: '#a0a0b0',
    fontSize: '11px',
    marginTop: '2px',
  },
  varLabel: {
    color: '#a0a0b0',
    fontSize: '12px',
  },
  benchValue: {
    color: '#ffffff',
    fontSize: '16px',
    fontWeight: 'bold',
  },
  benchValueVar: {
    color: '#ffffff',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  benchNone: {
    color: '#a0a0b0',
    fontSize: '14px',
  },
  tagManual: {
    display: 'inline-block',
    padding: '1px 7px',
    marginLeft: '8px',
    borderRadius: '5px',
    backgroundColor: '#5b3a8c',
    color: '#ffffff',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    verticalAlign: 'middle',
  },
  tagLive: {
    display: 'inline-block',
    padding: '1px 7px',
    marginLeft: '8px',
    borderRadius: '5px',
    backgroundColor: '#0f3460',
    color: '#a0a0b0',
    fontSize: '10px',
    fontWeight: 'bold',
    letterSpacing: '0.5px',
    verticalAlign: 'middle',
  },
};

function Benchmarks() {
  const isAdmin = localStorage.getItem('admin_role') === 'admin';
  const token = localStorage.getItem('admin_token');
  const [gameTypes, setGameTypes] = useState([]);
  const [selectedGame, setSelectedGame] = useState(null);
  const [benchmarks, setBenchmarks] = useState({});
  const [manualOverrides, setManualOverrides] = useState({});
  const [averages, setAverages] = useState({});
  const [expanded, setExpanded] = useState({});
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadGameTypes();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (selectedGame) {
      loadBenchmarks(selectedGame.id);
      loadAverages(selectedGame.id);
    }
  }, [selectedGame]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadGameTypes() {
    const res = await fetch(`${API}/game-types`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (Array.isArray(data)) {
      // Custom games use their original game's benchmark, so they aren't edited here.
      const official = data.filter(g => !g.is_custom);
      setGameTypes(official);
      if (official.length > 0) setSelectedGame(official[0]);
    }
  }

  async function loadBenchmarks(gameTypeId) {
    const res = await fetch(`${API}/admin/benchmarks/${gameTypeId}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    if (data.benchmarks) {
      const bMap = {};
      const mMap = {};
      data.benchmarks.forEach(b => {
        const key = b.v1 || b.v2 || b.v4
          ? `${b.code}|${b.v1 || ''}|${b.v2 || ''}|${b.v4 || ''}`
          : b.code;
        bMap[key] = b.target_seconds;
        mMap[key] = b.is_manual || false;
      });
      setBenchmarks(bMap);
      setManualOverrides(mMap);
    }
  }

  async function loadAverages(gameTypeId) {
    const res = await fetch(`${API}/admin/benchmarks/${gameTypeId}/averages`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    const data = await res.json();
    setAverages(data);
  }

  const isFrozen = averages?._meta?.source === 'frozen';
  const dimMap = (selectedGame?.form_config?.benchmark_dimensions) || FALLBACK_DIMENSIONS;
  const codeOrder = (() => {
    const ordered = PREFERRED_CODE_ORDER.filter(c => c in dimMap);
    Object.keys(dimMap).forEach(c => { if (!ordered.includes(c)) ordered.push(c); });
    return ordered;
  })();
  const hasCells = code => (dimMap[code] || []).length > 0;

  async function saveBenchmarks() {
    setLoading(true);
    setError('');
    try {
      const updates = [];
      codeOrder.forEach(code => {
        const key = code;
        if (manualOverrides[key]) {
          updates.push({
            code,
            target_seconds: parseFloat(benchmarks[key]) || 0,
            is_manual: true,
            v1: null, v2: null, v4: null,
          });
        }
        const avg = averages[code];
        if (avg?.variables) {
          Object.entries(avg.variables).forEach(([varKey, varData]) => {
            const fullKey = `${code}|${varKey}`;
            if (manualOverrides[fullKey]) {
              const { v1, v2, v4 } = comboParts(varKey);
              updates.push({
                code,
                target_seconds: parseFloat(benchmarks[fullKey]) || 0,
                is_manual: true,
                v1, v2, v4,
              });
            }
          });
        }
      });
      const res = await fetch(`${API}/admin/benchmarks/${selectedGame.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ benchmarks: updates }),
      });
      if (res.ok) {
        setMessage('Benchmarks saved successfully');
        setTimeout(() => setMessage(''), 3000);
      } else {
        setError('Failed to save benchmarks');
      }
    } catch (e) {
      setError('Could not connect to server');
    }
    setLoading(false);
  }

  function toggleExpand(code) {
    setExpanded(prev => ({ ...prev, [code]: !prev[code] }));
  }

  function toggleManual(key) {
    setManualOverrides(prev => ({ ...prev, [key]: !prev[key] }));
  }

  function updateBenchmark(key, value) {
    setBenchmarks(prev => ({ ...prev, [key]: value }));
  }

  // The running average of every test recorded so far — shown beside the benchmark
  // for reference, not used for scoring once a Benchmark Period has closed.
  function renderRunningAverage(avg, count, filteredCount) {
    return (
      <div>
        <span style={styles.autoValue}>
          {avg ? `${avg}s` : 'No data yet'}
        </span>
        {count > 0 && (
          <div style={styles.recordCount}>
            {filteredCount} of {count} records used
          </div>
        )}
      </div>
    );
  }

  // What reports actually compare against: a manual override if one is set,
  // otherwise the frozen Benchmark Period value (or the running average when no
  // period has closed).
  function renderBenchmarkValue(key, frozenValue, isManual, isVariable) {
    const valueStyle = isVariable ? styles.benchValueVar : styles.benchValue;
    if (isManual) {
      const typed = parseFloat(benchmarks[key]);
      return (
        <span style={valueStyle}>
          {typed > 0 ? `${typed}s` : '—'}
          <span style={styles.tagManual}>MANUAL</span>
        </span>
      );
    }
    if (frozenValue) {
      return (
        <span style={valueStyle}>
          {frozenValue}s
          {!isFrozen && <span style={styles.tagLive}>RUNNING AVERAGE</span>}
        </span>
      );
    }
    return <span style={styles.benchNone}>No benchmark yet</span>;
  }

  function renderCodeRow(code) {
    const avg = averages[code];
    const isManual = manualOverrides[code] || false;
    const hasVariables = hasCells(code);
    const varCount = avg?.variables ? Object.keys(avg.variables).length : 0;

    return (
      <React.Fragment key={code}>
        <tr>
          <td style={styles.td}>
            <div style={styles.codeLabel}>
              {code}
              {hasVariables && varCount > 0 && (
                <button
                  style={styles.expandButton}
                  onClick={() => toggleExpand(code)}
                >
                  {expanded[code] ? '▲ Hide' : `▼ ${varCount} combos`}
                </button>
              )}
            </div>
            <div style={styles.codeDesc}>{CODE_DESCRIPTIONS[code]}</div>
          </td>
          <td style={styles.td}>
            {renderBenchmarkValue(code, avg?.avg, isManual, false)}
          </td>
          <td style={styles.td}>
            <div style={styles.toggleRow}>
              <button
                style={isManual ? styles.toggleOn : styles.toggleOff}
                onClick={() => toggleManual(code)}
                disabled={!isAdmin}
              />
              {isManual && (
                <input
                  style={styles.input}
                  type="number"
                  step="0.1"
                  value={benchmarks[code] || ''}
                  onChange={e => updateBenchmark(code, e.target.value)}
                  placeholder="0.0"
                />
              )}
            </div>
          </td>
          <td style={styles.td}>
            {renderRunningAverage(
              avg?.running_avg !== undefined ? avg.running_avg : avg?.avg,
              avg?.count || 0, avg?.filtered_count || 0)}
          </td>
        </tr>

        {expanded[code] && avg?.variables && Object.entries(avg.variables).map(([varKey, varData]) => {
          const fullKey = `${code}|${varKey}`;
          const varManual = manualOverrides[fullKey] || false;
          return (
            <tr key={fullKey}>
              <td style={styles.tdVar}>
                <span style={styles.varLabel}>{varData.label}</span>
              </td>
              <td style={styles.tdVar}>
                {renderBenchmarkValue(fullKey, varData.avg, varManual, true)}
              </td>
              <td style={styles.tdVar}>
                <div style={styles.toggleRow}>
                  <button
                    style={varManual ? styles.toggleOn : styles.toggleOff}
                    onClick={() => toggleManual(fullKey)}
                    disabled={!isAdmin}
                  />
                  {varManual && (
                    <input
                      style={{ ...styles.input, width: '80px' }}
                      type="number"
                      step="0.1"
                      value={benchmarks[fullKey] || ''}
                      onChange={e => updateBenchmark(fullKey, e.target.value)}
                      placeholder="0.0"
                    />
                  )}
                </div>
              </td>
              <td style={styles.tdVar}>
                {renderRunningAverage(
                  varData.running_avg !== undefined ? varData.running_avg : varData.avg,
                  varData.count, varData.filtered_count)}
              </td>
            </tr>
          );
        })}
      </React.Fragment>
    );
  }

  return (
    <div style={styles.container}>
      <Sidebar active="Benchmarks" />
      <div style={styles.main}>
        <BenchmarkTabs current="process" />
        <div style={styles.pageTitle}>Process Benchmarks</div>
        <div style={styles.pageSubtitle}>
          The target time each process is measured against, game by game. Expand any code to see the
          targets for particular table conditions.
        </div>

        {message && <div style={styles.success}>{message}</div>}
        {error && <div style={styles.error}>{error}</div>}

        <div>
          {gameTypes.map(game => (
            <button
              key={game.id}
              style={selectedGame?.id === game.id ? styles.gameTabActive : styles.gameTab}
              onClick={() => setSelectedGame(game)}
            >
              {game.name}
            </button>
          ))}
        </div>

        {selectedGame && (
          <BenchmarkPicker
            gameTypeId={selectedGame.id}
            canEdit={isAdmin}
            onChange={() => loadAverages(selectedGame.id)}
          />
        )}

        {selectedGame && (
          <div style={styles.card}>
            <div style={styles.cardTitle}>{selectedGame.name} — Target Times</div>
            <div style={styles.cardSubtitle}>
              {isFrozen
                ? `Benchmark column: the stored targets from ${averages._meta.label}. `
                : 'Benchmark column: the running average, until a Benchmark Period is closed. '}
              A manual override replaces it. The running average on the right is every test recorded so
              far, for reference only.
            </div>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Code</th>
                  <th style={styles.th}>Benchmark{isFrozen ? ` — ${averages._meta.label}` : ''}</th>
                  <th style={styles.th}>Manual override</th>
                  <th style={styles.th}>Running average</th>
                </tr>
              </thead>
              <tbody>
                {codeOrder.map(code => renderCodeRow(code))}
              </tbody>
            </table>
            {isAdmin && (
              <button style={styles.saveButton} onClick={saveBenchmarks} disabled={loading}>
                {loading ? 'Saving...' : 'Save Benchmarks'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default Benchmarks;